import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import { authApi } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { useMockMode } from '../lib/mockData';

const CODE_LENGTH = 6;

export default function AccessCodePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setPendingAccessCode, setToken, setUserId } = useAuthStore();
  const isMock = useMockMode();

  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-fill depuis URL (?code=MICKEY)
  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (!urlCode) return;

    const chars = urlCode.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH);
    if (chars.length === 0) return;

    const filled = chars.split('').concat(Array(CODE_LENGTH - chars.length).fill(''));
    setCode(filled);

    // Auto-submit si code complet
    if (chars.length === CODE_LENGTH) {
      setTimeout(() => submitCode(chars), 600);
    } else {
      // Focus la prochaine case vide
      setTimeout(() => inputRefs.current[chars.length]?.focus(), 100);
    }
  }, []);

  const handleChange = (index: number, value: string) => {
    const char = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(-1);
    const newCode = [...code];
    newCode[index] = char;
    setCode(newCode);
    setError('');

    if (char && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every((c) => c !== '')) {
      submitCode(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        const newCode = [...code];
        newCode[index - 1] = '';
        setCode(newCode);
        inputRefs.current[index - 1]?.focus();
      } else {
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    }
    // Coller (paste) dans une case
    if (e.key === 'v' && (e.metaKey || e.ctrlKey)) return; // laisser le paste handler gérer
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const filled = pasted.split('').concat(Array(CODE_LENGTH - pasted.length).fill(''));
    setCode(filled);
    const nextEmpty = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[nextEmpty]?.focus();
    if (pasted.length === CODE_LENGTH) {
      submitCode(pasted);
    }
  };

  const submitCode = async (codeStr: string) => {
    if (isLoading) return;
    setIsLoading(true);
    setError('');

    if (isMock) {
      await new Promise((r) => setTimeout(r, 600));
      setToken('mock-token');
      setIsLoading(false);
      navigate('/dreams');
      return;
    }

    try {
      const result = await authApi.validateAccessCode(codeStr);
      if (result.status === 'new_user') {
        setPendingAccessCode(result.accessCodeId!);
        navigate('/create-pin');
      } else if (result.status === 'existing_user') {
        // Login direct sans PIN pour les utilisateurs existants
        const loginResult = await authApi.codeLogin(codeStr);
        setToken(loginResult.token);
        setUserId(loginResult.user.id);
        navigate('/dreams');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code invalide');
      setCode(Array(CODE_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setIsLoading(false);
    }
  };

  const codeStr = code.join('');
  const canSubmit = codeStr.length === CODE_LENGTH && !isLoading;

  return (
    <div className="min-h-screen flex flex-col px-6 py-8 safe-area-inset relative">
      <div className="fixed inset-0 bg-system-carbon-500 -z-20" />
      <div className="fixed inset-0 -z-10" style={{
        background: 'radial-gradient(ellipse at 50% 30%, rgba(254,254,254,0.04) 0%, transparent 60%)',
      }} />

      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-white/35 hover:text-white/60 transition-colors w-fit"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="text-sm">Retour</span>
      </motion.button>

      <div className="flex-1 flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 rounded-2xl glass-card-elevated flex items-center justify-center animate-pulse-glow">
              <Sparkles className="w-8 h-8 text-white/70" />
            </div>
          </div>

          <h1 className="text-2xl font-display font-bold text-center mb-2">
            Entrez votre code
          </h1>
          <p className="text-ivory-muted text-center mb-8">
            Saisissez le code d'accès reçu
          </p>

          {/* 6 cases */}
          <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
            {Array.from({ length: CODE_LENGTH }).map((_, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="text"
                maxLength={1}
                value={code[index]}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="pin-digit uppercase"
                style={{ fontFamily: 'monospace', letterSpacing: 0 }}
                disabled={isLoading}
                autoFocus={index === 0}
                autoComplete="off"
                autoCapitalize="characters"
              />
            ))}
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-400 text-sm text-center mb-4"
            >
              {error}
            </motion.p>
          )}

          {isLoading && (
            <div className="flex items-center justify-center gap-2 text-white/60 mb-4">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Vérification...</span>
            </div>
          )}

          {isMock && <p className="text-ivory-faint text-xs text-center mb-4">Mode démo — tapez n'importe quoi</p>}

          <button
            onClick={() => canSubmit && submitCode(codeStr)}
            disabled={!canSubmit}
            className="btn-primary w-full py-4"
          >
            Continuer
          </button>

          <p className="text-ivory-faint text-sm text-center mt-8">
            Vous n'avez pas de code ?{' '}
            <a href="https://sublym.org" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white/70 transition-colors">
              Obtenir un accès
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
